import React from "react";
import { FiEye, FiTrash2, FiUpload, FiCheck, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { useSelector } from "react-redux";
import StepCodeTag from "../../../components/shared/StepCodeTag";
import { BLUEPRINT_STEP_CODES } from "../../../constants/workflowStepCodes";

import { getOrderProductDisplayName } from "../../../utils/orderProductDisplay";

const getBlueprintProductDisplay = (blueprint, orderProducts, rowIndex) => {
    if (blueprint?.itemName?.trim()) {
        return blueprint.itemName.trim();
    }
    if (!orderProducts?.length) {
        return "—";
    }
    if (orderProducts.length === 1) {
        return getOrderProductDisplayName(orderProducts[0]);
    }
    const productAtIndex = orderProducts[rowIndex];
    if (productAtIndex) {
        return getOrderProductDisplayName(productAtIndex);
    }
    return orderProducts.map(getOrderProductDisplayName).filter(Boolean).join(", ");
};

const BlueprintTab = ({
    orderData,
    orderProducts = [],
    setAddBlueprintModalState,
    handleDeleteBlueprint,
    handleSubmitDraft,
    setApproveDraftModalState,
    setRejectDraftModalState,
    setSalesmanApproveModalState,
    setSalesmanRejectModalState,
    setCustomerApproveModalState,
    setCustomerRejectModalState,
    superadmin,
    manager,
    isRegularUser,
    artist,
    productionManager,
    hasProductionManagerAccess,
    staff,
    outright,
    checkedBlueprints,
    handleToggleBlueprintCheck,
    areAllBlueprintsChecked,
    handleBlueprintFieldChange,
}) => {
    const allBlueprintsChecked = areAllBlueprintsChecked ? areAllBlueprintsChecked() : false;
    const currentUser = useSelector((state) => state.user.currentUser);

    // Used for conditional UI in blueprint list (Approve/Reject Draft buttons are removed)
    const isCreatorOfFirstBlueprint =
        orderData?.principle === "68707e6617b78bce728f2253" &&
        orderData?.blueprint?.[0]?.createdBy._id === currentUser?._id;
    const canApproveRejectDraft =
        (superadmin || manager || hasProductionManagerAccess) &&
        ["uploadedDraft", "pending", "draft"].includes(orderData?.status) &&
        !isCreatorOfFirstBlueprint;

    return (
        <div className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
            {/* Enhanced Header Section */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2 flex-wrap">
                            <StepCodeTag code="B1" />
                            Blueprint Management
                        </h1>
                        <p className="text-gray-600 text-sm">
                            Upload, review, and manage project blueprints for order processing
                        </p>
                    </div>
                    
                    {/* Enhanced Action Buttons */}
                    <div className="flex flex-wrap gap-3">
                        {(artist || superadmin || manager || outright) && orderData?.status !== "completed" && (
                            <>
                                <button
                                    className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-orange-700 hover:to-orange-800 focus:outline-none focus:ring-4 focus:ring-orange-300 transform hover:scale-105 transition-all duration-200"
                                    onClick={() => setAddBlueprintModalState(true)}
                                >
                                    <StepCodeTag code={BLUEPRINT_STEP_CODES.uploadBlueprint} />
                                    <FiUpload className="h-5 w-5 group-hover:animate-bounce" />
                                    <span className="font-semibold">Upload Blueprint</span>
                                </button>

                                <button
                                    className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-4 focus:ring-purple-300 transform hover:scale-105 transition-all duration-200"
                                    onClick={handleSubmitDraft}
                                >
                                    <StepCodeTag code={BLUEPRINT_STEP_CODES.submitDraft} />
                                    <FiCheck className="h-5 w-5 group-hover:animate-pulse" />
                                    <span className="font-semibold">Submit Draft</span>
                                </button>
                            </>
                        )}
                        
                        {staff && ["pendingSalesmanDraft", "uploadedDraft"].includes(orderData?.status) && (
                            <>
                                <button
                                    className={`group flex items-center px-6 py-3 rounded-xl shadow-lg focus:outline-none focus:ring-4 transform transition-all duration-200 ${
                                        allBlueprintsChecked
                                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:shadow-xl hover:from-blue-700 hover:to-blue-800 focus:ring-blue-300 hover:scale-105'
                                            : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                    }`}
                                    onClick={() => allBlueprintsChecked && setSalesmanApproveModalState(true)}
                                    disabled={!allBlueprintsChecked}
                                    title={!allBlueprintsChecked ? "Please check all blueprints before approving" : "Salesman Approve"}
                                >
                                    <FiCheckCircle className={`mr-2 h-5 w-5 ${allBlueprintsChecked ? 'group-hover:animate-pulse' : ''}`} />
                                    <span className="font-semibold">Salesman Approve</span>
                                </button>
                                
                                <button
                                    className="group flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-4 focus:ring-red-300 transform hover:scale-105 transition-all duration-200"
                                    onClick={() => setSalesmanRejectModalState(true)}
                                >
                                    <FiXCircle className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                                    <span className="font-semibold">Salesman Reject</span>
                                </button>
                            </>
                        )}
                        
                        {(superadmin || manager || isRegularUser) && ["pendingCustomerDraft", "pendingSalesmanDraft"].includes(orderData?.status) && (
                            <>
                                <button
                                    className={`group flex items-center px-6 py-3 rounded-xl shadow-lg focus:outline-none focus:ring-4 transform transition-all duration-200 ${
                                        allBlueprintsChecked
                                            ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:shadow-xl hover:from-green-700 hover:to-green-800 focus:ring-green-300 hover:scale-105'
                                            : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                                    }`}
                                    onClick={() => allBlueprintsChecked && setCustomerApproveModalState(true)}
                                    disabled={!allBlueprintsChecked}
                                    title={!allBlueprintsChecked ? "Please check all blueprints before approving" : "Customer Approve"}
                                >
                                    <FiCheckCircle className={`mr-2 h-5 w-5 ${allBlueprintsChecked ? 'group-hover:animate-pulse' : ''}`} />
                                    <span className="font-semibold">Customer Approve</span>
                                </button>
                                
                                <button
                                    className="group flex items-center px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-4 focus:ring-red-300 transform hover:scale-105 transition-all duration-200"
                                    onClick={() => setCustomerRejectModalState(true)}
                                >
                                    <FiXCircle className="mr-2 h-5 w-5 group-hover:animate-pulse" />
                                    <span className="font-semibold">Customer Reject</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Enhanced Blueprint Files Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-orange-400 to-orange-500 text-white py-4 px-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold flex items-center gap-2 flex-wrap">
                            <FiUpload className="h-5 w-5" />
                            Blueprint Files
                        </h3>
                        <div className="bg-black px-3 py-1 rounded-full text-sm font-medium">
                            {orderData?.blueprint?.length || 0} files
                        </div>
                    </div>
                </div>

                {orderData?.blueprint && orderData.blueprint.length > 0 ? (
                    <div className="w-full overflow-x-auto">
                        <table className="w-full min-w-max border-collapse">
                            <thead>
                                <tr className="bg-gradient-to-r from-gray-800 to-gray-900 text-white">
                                    {(canApproveRejectDraft) ||
                                    (staff && orderData?.status === "pendingSalesmanDraft") ||
                                    ((superadmin || manager || isRegularUser) && orderData?.status === "pendingCustomerDraft") ? (
                                        <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider border-r border-gray-700" style={{ width: '5%' }}>
                                            <FiCheckCircle className="w-5 h-5 mx-auto" />
                                        </th>
                                    ) : null}
                                    <th className="hidden md:table-cell px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700" style={{ width: '5%' }}>
                                        #
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700" style={{ width: '35%' }}>
                                        File Name
                                    </th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider border-r border-gray-700" style={{ width: '35%' }}>
                                        Product
                                    </th>
                                    <th className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ width: '15%' }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {orderData.blueprint.map((blueprint, index) => (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-50 transition-colors duration-150"
                                    >
                                        {(canApproveRejectDraft) ||
                                        (staff && orderData?.status === "pendingSalesmanDraft") ||
                                        ((superadmin || manager || isRegularUser) && orderData?.status === "pendingCustomerDraft") ? (
                                            <td className="px-3 py-3 whitespace-nowrap text-center border-r border-gray-200">
                                                <input
                                                    type="checkbox"
                                                    checked={checkedBlueprints[blueprint._id] || false}
                                                    onChange={() => handleToggleBlueprintCheck(blueprint._id)}
                                                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                                                />
                                            </td>
                                        ) : null}
                                        <td className="hidden md:table-cell px-3 py-3 whitespace-nowrap border-r border-gray-200">
                                            <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full text-sm font-bold">
                                                {index + 1}
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap border-r border-gray-200">
                                            <div className="text-sm font-semibold text-gray-900">
                                                {blueprint.name}
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 border-r border-gray-200">
                                            <div className="text-sm font-medium text-gray-900">
                                                {getBlueprintProductDisplay(blueprint, orderProducts, index)}
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 whitespace-nowrap text-center">
                                            <div className="flex justify-center gap-2 flex-nowrap">
                                                <a
                                                    href={blueprint.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex"
                                                >
                                                    <button className="group inline-flex items-center justify-center gap-1 w-[80px] h-9 md:w-auto md:min-w-[80px] md:h-9 md:px-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md text-xs font-medium">
                                                        <FiEye className="w-3 h-3 flex-shrink-0 group-hover:animate-pulse" />
                                                        <span>View</span>
                                                    </button>
                                                </a>
                                                {(artist || superadmin || manager) && (
                                                    <button
                                                        className="group inline-flex items-center justify-center gap-1 w-[80px] h-9 md:w-auto md:min-w-[80px] md:h-9 md:px-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md text-xs font-medium"
                                                        onClick={() =>
                                                            handleDeleteBlueprint({
                                                                id: orderData._id,
                                                                blueprintId: blueprint._id,
                                                            })
                                                        }
                                                    >
                                                        <FiTrash2 className="w-3 h-3 flex-shrink-0 group-hover:animate-pulse" />
                                                        <span>Delete</span>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 mb-4">
                            <FiUpload className="h-12 w-12 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Blueprints Yet</h3>
                        <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                            {(artist || superadmin || manager || outright)
                                ? "Upload your first blueprint to get started with the order processing workflow."
                                : "No blueprints have been uploaded for this order yet."
                            }
                        </p>
                        {(artist || superadmin || manager || outright) && orderData?.status !== "completed" && (
                            <button
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-4 focus:ring-blue-300 transform hover:scale-105 transition-all duration-200"
                                onClick={() => setAddBlueprintModalState(true)}
                            >
                                <StepCodeTag code={BLUEPRINT_STEP_CODES.uploadBlueprint} />
                                <FiUpload className="h-5 w-5" />
                                <span className="font-semibold">Upload Blueprint</span>
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Customer Signature Section */}
            {orderData?.customerApprovalSignature && (
                <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 mt-8">
                    <div className="flex items-center mb-6">
                        <FiCheckCircle className="h-6 w-6 text-green-600 mr-3" />
                        <h3 className="text-xl font-bold text-gray-900">Customer Signature</h3>
                    </div>
                    
                    <div className="border-2 border-gray-300 rounded-lg p-4 bg-gray-50">
                        <img
                            src={orderData.customerApprovalSignature}
                            alt="Customer Signature"
                            className="max-w-full h-auto mx-auto"
                            style={{ maxHeight: '200px' }}
                        />
                    </div>
                    
                    <p className="text-sm text-gray-600 mt-4 text-center">
                        Signature provided by {orderData.userId?.name || 'Customer'} for blueprint approval
                    </p>
                </div>
            )}
        </div>
    );
};

export default BlueprintTab;
